import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useBrand } from '../BrandProvider.js';
import { BrandWordmark } from '../BrandWordmark.js';
import { AvatarImage } from './AvatarImage.js';
import { ThemeToggleRow, ThemeCornerButton } from './ThemeControl.js';
import { NotificationCenter } from './NotificationCenter.js';
import { WelcomeModal } from './WelcomeModal.js';
import { titleCaseName } from '../util/text.js';
import { useAuth, logoutRedirect } from '../AuthProvider.js';

interface NavItem {
  label: string;
  /** Absent on a group parent — parents are pure menu triggers, never routes (04 #93: every admin route stays flat). */
  to?: string;
  adminOnly?: boolean;
  /** Child links. Presence makes this a group: a desktop dropdown / a mobile accordion section.
      The grouping itself is expressed by the CALLER (adminSections.ts) — AppShell only renders
      whatever it is handed, so the client portal's flat items go through the same code they always did. */
  children?: NavItem[];
}

interface AppShellProps {
  children: ReactNode;
  navItems?: NavItem[];
}

/** The mobile drawer's reveal anchor — the profile chip's approximate center in the header
 *  (px-6 pad + half the 30px avatar; py-4 pad + half). Both the open and close animations
 *  grow/collapse a circle from this point (s103: "the menu originates from the chip"). */
const REVEAL_ANCHOR = '40px 32px';
const REVEAL_MS = 340;
const REVEAL_EASE = 'cubic-bezier(0.35, 0.7, 0.3, 1)';
/** The SWEEP (s103a, owner-ruled): the open-state circle deliberately stops short so its arc
 *  stays visible as the panel's bottom edge — full height at the bottom-left, sweeping up to
 *  exactly this many px of cut at the panel's right edge. The radius is computed in PX from the
 *  live viewport at each open (a fixed % basis under-covered tall phones and clipped the bottom
 *  rows — the original bug this replaces); the bottom section's pb-16 keeps its rows above the
 *  arc, so Sign out (and later the Dark Mode row) sit ON the sweep, never under it. */
const SWEEP_DEPTH_PX = 56;
function computeRevealRadius(): number {
  const panelWidth = Math.min(window.innerWidth * 0.8, 270);
  return Math.hypot(panelWidth - 40, window.innerHeight - 32 - SWEEP_DEPTH_PX);
}

export function AppShell({ children, navItems = [] }: AppShellProps): JSX.Element {
  const { brand } = useBrand();
  const { user, memberSince, adminSurface, activeClient } = useAuth();
  const location = useLocation();

  // Desktop grouped nav (s103): hover PEEKS a menu (grace-delayed close so diagonal travel into
  // the menu never drops it), click PINS it (a pinned menu ignores hover-leave and closes only on
  // a second click, a click elsewhere, or opening another parent).
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [pinnedGroup, setPinnedGroup] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mobile chip drawer (s103): replaces the old hamburger + stacked-links drawer.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [revealR, setRevealR] = useState(1000);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  /* Close the account menu on any outside click. A fixed inset-0 backdrop cannot do this
     here: .glass-header's backdrop-filter makes the header a CONTAINING BLOCK for fixed
     descendants, so an inset-0 layer only covers the header strip and clicks below it
     never land (s104 verification round 2). Document-level listener instead, the same
     pattern NotificationCenter uses. */
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);
  // Drag-to-close (s103): a leftward drag on the drawer slides it out; under the threshold it
  // springs back. The circle reveal is reserved for tap-open/tap-close; a completed drag slides.
  const [dragX, setDragX] = useState(0);
  const [dragClosing, setDragClosing] = useState(false);
  const [noAnim, setNoAnim] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; active: boolean } | null>(null);

  // adminOnly nav shows only where the admin surface is permitted (08 Q10:
  // a platform-admin on a non-admin host gets the client view, no admin nav).
  const visibleNav = navItems.filter((n) => !n.adminOnly || adminSurface);
  const hasGroups = visibleNav.some((n) => n.children && n.children.length > 0);

  const clearMenus = (): void => {
    setOpenGroup(null);
    setPinnedGroup(null);
  };
  const closeDrawer = (): void => setDrawerOpen(false);
  const openDrawer = (): void => {
    setRevealR(computeRevealRadius()); // re-measured per open: rotation/resize safe
    setDrawerOpen(true);
  };

  // When the drawer opens, pre-expand the group that contains the current page (or none).
  useEffect(() => {
    if (!drawerOpen) return;
    const current = navItems.find((n) => n.children?.some((c) => c.to && location.pathname.startsWith(c.to)));
    setOpenMobileGroup(current ? current.label : null);
  }, [drawerOpen, location.pathname, navItems]);

  const onDrawerPointerDown = (e: React.PointerEvent<HTMLElement>): void => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, active: false };
  };
  const onDrawerPointerMove = (e: React.PointerEvent<HTMLElement>): void => {
    const d = dragRef.current;
    if (!d || dragClosing) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.active) {
      if (dx < -12 && Math.abs(dx) > Math.abs(dy)) {
        d.active = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      } else {
        return;
      }
    }
    setDragX(Math.min(0, dx));
  };
  const onDrawerPointerEnd = (): void => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d?.active || dragClosing) return;
    if (dragX < -60) {
      // Past the threshold: finish as a slide-out, then reset with the transitions suppressed for
      // one frame so the circle-collapse animation does not replay over the already-hidden panel.
      setDragClosing(true);
      setTimeout(() => {
        setNoAnim(true);
        setDrawerOpen(false);
        setDragClosing(false);
        setDragX(0);
        setTimeout(() => setNoAnim(false), 60);
      }, 210);
    } else {
      setDragX(0);
    }
  };

  const drawerStyle: CSSProperties = {
    clipPath: drawerOpen ? `circle(${revealR}px at ${REVEAL_ANCHOR})` : `circle(0px at ${REVEAL_ANCHOR})`,
    transform: dragClosing ? 'translateX(-105%)' : dragX !== 0 ? `translateX(${dragX}px)` : undefined,
    transition: noAnim
      ? 'none'
      : dragClosing
        ? 'transform 200ms ease-in'
        : dragX !== 0
          ? `clip-path ${REVEAL_MS}ms ${REVEAL_EASE}`
          : `clip-path ${REVEAL_MS}ms ${REVEAL_EASE}, transform 200ms ease-out`,
    touchAction: 'pan-y',
  };

  const avatarSrc = user?.photoBlobRef ? `/api/avatar?userId=${encodeURIComponent(user.id)}` : null;

  const chevron = (open: boolean): JSX.Element => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={'transition-transform ' + (open ? 'rotate-180' : '')}
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-background)] text-[var(--color-text)] font-[var(--font-body)]">
      <header className="border-b border-[rgb(var(--edge-rgb)/0.10)] glass-header sticky top-0 z-10">
        {/* s103 mobile header swap (owner-ruled amendment to the 04 #3 paired header, which now
            applies at md+ in full): on mobile the LEFT is the user chrome (profile chip = the menu
            trigger, then the bell) and the RIGHT is the brand chrome as LOGOS ONLY (brand | client),
            falling back to name text only when a logo is missing. Flex `order` re-seats the single
            NotificationCenter instance per breakpoint (mounting it twice would double its fetches). */}
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3 md:gap-6">
          {/* Mobile menu trigger: the profile chip (avatar + a small menu glyph). */}
          {user && visibleNav.length > 0 ? (
            <button
              type="button"
              onClick={openDrawer}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              className="md:hidden order-1 inline-flex items-center gap-1.5 rounded-full border border-[rgb(var(--edge-rgb)/0.10)] py-0.5 pl-0.5 pr-2 hover:bg-[rgb(var(--edge-rgb)/0.05)] transition shrink-0"
            >
              <AvatarImage src={avatarSrc} name={user.displayName} size={30} alt="" />
              <span className="flex flex-col gap-[3px]" aria-hidden="true">
                <span className="block w-3.5 h-0.5 rounded bg-[var(--color-muted)]" />
                <span className="block w-3.5 h-0.5 rounded bg-[var(--color-muted)]" />
                <span className="block w-3.5 h-0.5 rounded bg-[var(--color-muted)]" />
              </span>
            </button>
          ) : null}

          {/* Brand block — the full 04 #3 paired header. Desktop always; mobile only when signed out. */}
          <Link to="/" className={(user ? 'hidden md:flex' : 'flex') + ' items-center gap-3 group md:order-1'}>
            <span className="font-display text-lg tracking-tight">
              <BrandWordmark />
            </span>
            {brand?.branding.logoUrl ? (
              <img src={brand.branding.logoUrl} alt={brand.name} className="h-8 w-auto" />
            ) : (
              <div className="h-8 w-8 rounded bg-[var(--color-primary)]" />
            )}
            {activeClient && activeClient.clientType === 'business' ? (
              <>
                <span className="text-[rgb(var(--edge-rgb)/0.20)] text-xl font-light" aria-hidden="true">|</span>
                {activeClient.logoBlobRef ? (
                  <AvatarImage
                    src={`/api/client-logo?clientId=${encodeURIComponent(activeClient.clientId)}`}
                    name={activeClient.displayName}
                    size={32}
                    shape="square"
                    alt={`${activeClient.displayName} logo`}
                  />
                ) : null}
                <span className="font-display text-lg tracking-tight">{activeClient.displayName}</span>
              </>
            ) : null}
          </Link>

          {/* Desktop nav. Grouped (admin, s103): six top-level items always fit, so NO scroll
              container — an open dropdown would be clipped by an overflow-x-auto ancestor (one
              overflow axis being non-visible forces the other non-visible too). Flat (client):
              the original scroll container, unchanged. */}
          {hasGroups ? (
            <nav className="hidden md:flex items-center gap-1 min-w-0 flex-1 mx-2 md:order-2">
              {pinnedGroup ? (
                <div className="fixed inset-0 z-40" onClick={clearMenus} aria-hidden="true" />
              ) : null}
              {visibleNav.map((n) =>
                n.children && n.children.length > 0 ? (
                  <div
                    key={n.label}
                    className="relative shrink-0"
                    onMouseEnter={() => {
                      if (closeTimer.current) clearTimeout(closeTimer.current);
                      if (!pinnedGroup) setOpenGroup(n.label);
                    }}
                    onMouseLeave={() => {
                      if (pinnedGroup) return;
                      closeTimer.current = setTimeout(() => {
                        setOpenGroup((g) => (g === n.label ? null : g));
                      }, 150);
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={openGroup === n.label}
                      onClick={() => {
                        if (pinnedGroup === n.label) {
                          clearMenus();
                        } else {
                          setOpenGroup(n.label);
                          setPinnedGroup(n.label);
                        }
                      }}
                      className={
                        'px-3 py-1.5 text-sm rounded transition whitespace-nowrap flex items-center gap-1 z-50 relative ' +
                        (pinnedGroup === n.label ? 'bg-[rgb(var(--edge-rgb)/0.10)]' : openGroup === n.label ? 'bg-[rgb(var(--edge-rgb)/0.05)]' : 'hover:bg-[rgb(var(--edge-rgb)/0.05)]')
                      }
                    >
                      {n.label}
                      {chevron(openGroup === n.label)}
                    </button>
                    {openGroup === n.label ? (
                      /* pt-1 INSIDE the hover area (not a margin) — no dead gap for the pointer
                         to fall through while travelling from the tab into the menu. */
                      <div className="absolute left-0 top-full pt-1 z-50">
                        <div className="min-w-[11.5rem] rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-raised)] shadow-lg py-1">
                          {n.children.map((c) =>
                            c.to ? (
                              <Link
                                key={c.to}
                                to={c.to}
                                onClick={clearMenus}
                                className="block px-3 py-2 text-sm hover:bg-[rgb(var(--edge-rgb)/0.05)] transition whitespace-nowrap"
                              >
                                {c.label}
                              </Link>
                            ) : null,
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : n.to ? (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="px-3 py-1.5 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition whitespace-nowrap shrink-0"
                  >
                    {n.label}
                  </Link>
                ) : null,
              )}
            </nav>
          ) : visibleNav.length > 0 ? (
            /* Flat nav: absorbs overflow into its OWN horizontal scroll (min-w-0 flex-1) rather
               than letting the header row clip it, with a right-edge fade so it's obvious there's
               more to scroll — the last tab visibly softens/clips instead of ending cleanly. When
               the tabs fit, the fade falls over empty space and is invisible. */
            <nav className="hidden md:block relative min-w-0 flex-1 mx-2 md:order-2">
              <style>{`.nav-scroll::-webkit-scrollbar{display:none}`}</style>
              <div
                className="nav-scroll flex items-center gap-1 overflow-x-auto"
                style={{
                  scrollbarWidth: 'none',
                  WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 28px), transparent 100%)',
                  maskImage: 'linear-gradient(to right, black calc(100% - 28px), transparent 100%)',
                }}
              >
                {visibleNav.map((n) =>
                  n.to ? (
                    <Link
                      key={n.to}
                      to={n.to}
                      className="px-3 py-1.5 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition whitespace-nowrap shrink-0"
                    >
                      {n.label}
                    </Link>
                  ) : null,
                )}
              </div>
            </nav>
          ) : null}

          {/* Bell + account. One NotificationCenter instance: order-2 seats it beside the chip on
              mobile; md:order-3 returns it to the right on desktop. The account (name) menu is
              desktop-only — on mobile, Sign out lives in the drawer. */}
          {user ? (
            <div className="flex items-center gap-3 order-2 md:order-3">
              <NotificationCenter />
              <div className="hidden md:block relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-expanded={userMenuOpen}
                  aria-label="Account menu"
                  className="flex flex-col items-end leading-tight px-2 py-1 rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                >
                  <span className="text-sm text-[var(--color-text)] flex items-center gap-1">
                    {titleCaseName(user.displayName)}
                    {chevron(userMenuOpen)}
                  </span>
                  {memberSince ? (
                    <span className="hidden sm:block text-xs text-[var(--color-muted)]">
                      Member since {new Date(memberSince).getFullYear()}
                    </span>
                  ) : null}
                </button>
                {userMenuOpen ? (
                  <>
                    <div className="absolute right-0 top-full mt-1 min-w-[11rem] rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-raised)] shadow-lg py-1 z-50">
                      {/* 04 #95: the theme row sits ABOVE Sign out with a divider before it
                          (the 02d s103-addendum placement, executed by the dark-mode slice). */}
                      <ThemeToggleRow variant="menu" />
                      <div className="my-1 border-t border-[rgb(var(--edge-rgb)/0.10)]" aria-hidden="true" />
                      <button
                        type="button"
                        onClick={logoutRedirect}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                      >
                        Sign out
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Mobile brand chrome: logos only, right-aligned — brand logo | client logo — with name
              text ONLY where a logo is missing (s103 ruling; the full paired header stays at md+). */}
          {user ? (
            <Link to="/" className="md:hidden order-3 ml-auto flex items-center gap-2 min-w-0 shrink">
              {brand?.branding.logoUrl ? (
                <img src={brand.branding.logoUrl} alt={brand.name} className="h-7 w-auto" />
              ) : (
                <span className="font-display text-base tracking-tight whitespace-nowrap">
                  <BrandWordmark />
                </span>
              )}
              {activeClient && activeClient.clientType === 'business' ? (
                <>
                  <span className="text-[rgb(var(--edge-rgb)/0.20)] text-lg font-light" aria-hidden="true">|</span>
                  {activeClient.logoBlobRef ? (
                    <AvatarImage
                      src={`/api/client-logo?clientId=${encodeURIComponent(activeClient.clientId)}`}
                      name={activeClient.displayName}
                      size={28}
                      shape="square"
                      alt={`${activeClient.displayName} logo`}
                    />
                  ) : (
                    <span className="font-display text-sm tracking-tight truncate max-w-[8rem]">
                      {activeClient.displayName}
                    </span>
                  )}
                </>
              ) : null}
            </Link>
          ) : null}
        </div>
      </header>

      {/* Mobile drawer (s103): the chip-origin circle reveal. Portaled to document.body per the
          locked s24 overlay standard (never trust an in-header fixed context for a full-height
          panel). Both layers stay mounted so the open AND close animations play. */}
      {user && visibleNav.length > 0
        ? createPortal(
            <>
              <div
                className={
                  'fixed inset-0 z-40 bg-black/30 md:hidden transition-opacity duration-300 ' +
                  (drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none')
                }
                onClick={closeDrawer}
                aria-hidden="true"
              />
              <aside
                role="dialog"
                aria-label="Menu"
                aria-hidden={!drawerOpen}
                style={drawerStyle}
                className={
                  'fixed inset-y-0 left-0 z-50 w-4/5 max-w-[270px] md:hidden flex flex-col bg-[var(--surface-raised)] border-r border-[rgb(var(--edge-rgb)/0.10)] shadow-2xl ' +
                  (drawerOpen ? '' : 'pointer-events-none')
                }
                onPointerDown={onDrawerPointerDown}
                onPointerMove={onDrawerPointerMove}
                onPointerUp={onDrawerPointerEnd}
                onPointerCancel={onDrawerPointerEnd}
              >
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Close menu"
                  className="absolute top-2.5 right-2.5 h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
                {/* The user block doubles as a close target (s103 ruling: tapping the top where the
                    name is closes the menu, not just the X). */}
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="w-full text-left flex items-center gap-2.5 px-4 pt-4 pb-3 border-b border-[rgb(var(--edge-rgb)/0.10)] hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                >
                  <AvatarImage src={avatarSrc} name={user.displayName} size={40} alt="" />
                  <span className="min-w-0 pr-8">
                    <span className="block text-[15px] font-medium leading-tight truncate">
                      {titleCaseName(user.displayName)}
                    </span>
                    {memberSince ? (
                      <span className="block text-xs text-[var(--color-muted)]">
                        Member since {new Date(memberSince).getFullYear()}
                      </span>
                    ) : null}
                  </span>
                </button>
                <nav className="flex-1 overflow-y-auto px-2.5 py-1.5 flex flex-col">
                  {visibleNav.map((n) =>
                    n.children && n.children.length > 0 ? (
                      <div key={n.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => setOpenMobileGroup((g) => (g === n.label ? null : n.label))}
                          aria-expanded={openMobileGroup === n.label}
                          className="flex items-center justify-between px-2 py-2.5 text-sm font-medium rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                        >
                          {n.label}
                          {chevron(openMobileGroup === n.label)}
                        </button>
                        {openMobileGroup === n.label
                          ? n.children.map((c) =>
                              c.to ? (
                                <Link
                                  key={c.to}
                                  to={c.to}
                                  onClick={closeDrawer}
                                  className="py-2.5 pl-6 pr-2 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                                >
                                  {c.label}
                                </Link>
                              ) : null,
                            )
                          : null}
                      </div>
                    ) : n.to ? (
                      <Link
                        key={n.to}
                        to={n.to}
                        onClick={closeDrawer}
                        className="px-2 py-2.5 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition"
                      >
                        {n.label}
                      </Link>
                    ) : null,
                  )}
                </nav>
                {/* Sign out: the bottom row, styled like every other row, divider above, exit icon
                    (s103). pb-16 keeps the rows above the sweep arc (SWEEP_DEPTH_PX cuts 56px at
                    the panel's right edge; 64px of padding clears it across the full row width).
                    04 #95: the theme row rides here ABOVE Sign out, per the 02d s103 addendum. */}
                <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] px-2.5 pt-2 pb-16">
                  <ThemeToggleRow variant="drawer" />
                  <button
                    type="button"
                    onClick={logoutRedirect}
                    className="w-full flex items-center gap-2.5 px-2 py-2.5 text-sm rounded hover:bg-[rgb(var(--edge-rgb)/0.05)] transition text-left"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <path d="m16 17 5-5-5-5" />
                      <path d="M21 12H9" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </aside>
            </>,
            document.body,
          )
        : null}

      <main className="flex-1 relative z-0">
        {/* z-0 makes the page a self-contained stacking context: per-page card z-indexes
            (Documents/Invoices use z-20..z-40 for their own internal layering) can never rise
            above the sticky header again. Modals portal to document.body and are unaffected. */}
        <div className="max-w-7xl mx-auto px-6 py-8">{children}</div>
      </main>

      <footer className="border-t border-[rgb(var(--edge-rgb)/0.10)] py-6 text-center text-xs text-[var(--color-muted)]">
        © {new Date().getFullYear()} {brand?.name ?? ''}
      </footer>

      {/* 04 #95 signed-out theme control (owner, s104): one adaptive corner disc, only on
          the signed-out shell. Signed-in users have the menu rows instead. */}
      {!user ? <ThemeCornerButton /> : null}

      <WelcomeModal />
    </div>
  );
}
